import React, { useEffect, useRef, useState } from 'react';
import { Flex, Box, Text } from 'rebass';
import useEntity from 'entities/hooks/use-entity';
import { useDropzone } from 'react-dropzone';
import TextLayer from 'app/payment/TextLayer';
import ImageLayer from 'app/payment/ImageLayer';
import ReactAvatarEditor from 'react-avatar-editor';
import ImageEditingOptions from './ImageEditingOptions';
import Modal from 'app/modal/v3/index';
import ImageUploader from './ImageUploader';
import { postMedium } from 'story-media/api';
import { uploadMedium } from 'story-media/upload-medium';
import { destroy, getSWR, patch } from 'utils/api';
import useSWR from 'swr';
import { LoadingBg } from 'collections/watch-me-grow/components/PreviewNode';
import RotatingText from './RotatingText';
import useEntityActionCreators from 'entities/hooks/use-entity-action-creators';
import { FiImage, FiEdit } from "react-icons/fi";
import { imageUrlToBlob, renderPositionForVGBImage, scalePosition } from 'collections/watch-me-grow/components/ImageEditor/helper';

export function nestImageLayers(layers) {
    const layerMap = new Map();

    // Clone layers and add children array
    layers.forEach(layer => {
        layerMap.set(layer.id, {
            ...layer,
            children: []
        });
    });

    const result = [];

    layers.forEach(layer => {
        const destination =
            layer.relationships?.["event-destination-layer"]?.data;

        // If this layer has event-destination-layer,
        // it becomes the parent of that target layer
        if (destination?.id) {
            const parent = layerMap.get(layer.id);
            const child = layerMap.get(destination.id);

            if (parent && child) {
                parent.children.push(child);
            }

            result.push(parent);
        } else {
            // Only add layers that are not already nested as children
            const isChild = layers.some(
                l =>
                    l.relationships?.["event-destination-layer"]?.data?.id === layer.id
            );

            if (!isChild) {
                result.push(layerMap.get(layer.id));
            }
        }
    });

    return result;
}

function RenderImageLayer({ zoom, setTmpURL, tmpURL, selectedTemplate, setIsDefaultStepSet, destinationImageSrcExists, setDestinationImageSrcExists, setPhotoLayerOriginal, firstBookId, coverId, showHover, photoLayerOriginal, setSelectedLayer, selectedLayer, frameLayer, photoLayer, setPhotoLayer, photoSrc, setPhotoSrc, forceUploadMode, setForceUploadMode, children, ...props }) {

    const [isSubmitting, setSubmitting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [scale, setScale] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showEditor, setShowEditor] = useState(false);
    const [photoScaling, setPhotoScalling] = useState(null);
    const [file, setFile] = useState(null);
    const [src, setSrc] = useState(null);
    const [originalImage, setOriginalImage] = useState(null);

    const [uploadStatus, setUploadStatus] = useState(null);
    const isUploadingNewRef = useRef(false);

    const { entity: frontCoverEntity } = useEntity({ id: coverId, type: 'front-covers' })
    const { entity: imageSrc } = useEntity({ type: 'images', id: props?.['photo'].id });

    useEffect(() => {
        const doesExist = (children || []).find(child => child?.attributes?.['component-value'] === "photo")
        if (doesExist) {
            setDestinationImageSrcExists(doesExist)
        }
    }, [children.length, selectedTemplate])

    const { entity: destinationImageSrc } = useEntity({ type: 'images', id: originalImage?.id });

    const doesImageExist = Object.keys(imageSrc || {}).length > 0

    const [refreshInterval, setRefreshInterval] = useState(0);
    const { revalidate: revalidateCover } = useSWR(`/v3/books/${firstBookId}/front_covers/${coverId}`, getSWR, { refreshInterval });
    // const { data, revalidate } = useSWR(destinationCanvasId ? `/v2/media/${destinationCanvasId}` : null, getSWR, { refreshInterval });
    const { data: dataImage, revalidate: revalidateImage } = useSWR(originalImage?.id ? `/v2/media/${originalImage?.id}` : null, getSWR, { refreshInterval });

    const updateMediaDetailsImage = { id: dataImage?.data?.data?.id, ...dataImage?.data?.data?.attributes };

    const { receiveDeletion, update } = useEntityActionCreators();

    useEffect(() => {
        setUploadStatus(updateMediaDetailsImage?.state);
    }, [updateMediaDetailsImage?.state]);

    useEffect(() => {
        if (
            uploadStatus === 'pending_upload' ||
            uploadStatus === 'uploaded' ||
            uploadStatus === 'processing'
        ) {
            setRefreshInterval(3000);
        }

        if (refreshInterval !== null && uploadStatus === 'finished') {
            setRefreshInterval(0);
            setSubmitting(false);
            revalidateCover();
        }
    }, [uploadStatus, setRefreshInterval]);

    const isCurrentLayerSelected = selectedLayer?.id === props?.id

    const onDrop = async (val) => {
        if (val && val[0].type.includes('image')) {
            setFile(val[0]);
            setPhotoSrc(val[0])
        }
    };

    useEffect(() => {
        if (!isUploadingNewRef.current && destinationImageSrcExists?.relationships?.['photo']?.data?.id !== originalImage?.id)
            setOriginalImage(destinationImageSrcExists?.relationships?.['photo']?.data)
        if (isCurrentLayerSelected && doesImageExist)
            setSrc(imageSrc?.['image-versions']?.[4]?.url);
        else
            onImageReady()
    }, [isCurrentLayerSelected, doesImageExist, imageSrc, destinationImageSrcExists]);


    useEffect(() => {
        setFile(destinationImageSrc?.tmpURL || destinationImageSrc?.['image-versions']?.[4]?.url || destinationImageSrc?.['asset-url'])
    }, [destinationImageSrc]);

    useEffect(() => {
        if (isCurrentLayerSelected) {
            setIsModalOpen(true);
        }
    }, [isCurrentLayerSelected]);

    const uploaderSrc = forceUploadMode ? undefined : (destinationImageSrcExists ? file : undefined);

    const { getRootProps, getInputProps } = useDropzone({ onDrop });

    const onImageSelection = async (val, selectedFile) => {
        const { scale: valScale, position, croppedPostion, croppedBlob = null } = val;

        const attributes = {
            'crop-left': `${croppedPostion?.x}%`,
            'crop-top': `${croppedPostion?.y}%`,
            left: position?.x * photoLayer?.width,
            top: position?.y * photoLayer?.height,
            scale: +valScale,
            'crop-width': photoLayer?.['crop-width'],
            'crop-height': photoLayer?.['crop-height'],
        };
        if (croppedPostion.width || croppedPostion.height) {
            attributes['crop-width'] = `${croppedPostion.width}%`;
            attributes['crop-height'] = `${croppedPostion.height}%`;
        }
        if (selectedFile && selectedLayer)
            setUploadStatus('pending_upload');
        setSubmitting(true);
        setShowEditor(false);

        let canvasUploadUrl = null;
        let blob = null;
        const updatedAttrs = {};

        try {
            // if (croppedBlob) {
            //     blob = new Blob([croppedBlob], { type: 'image/png' });
            //     blob.name = 'canvas-clip-image.png';

            //     const payload = await postMedium({
            //         attrs: {
            //             type: 'Image',
            //             ownerType: "CelebrationPrintableLayer",
            //             ownerId: photoLayer.id,
            //             filename: blob.name,
            //             previewFilename: ' ',
            //         },
            //     });
            //     const { entities, result } = payload.data;
            //     const { 'upload-url': uploadUrl } = entities.images[result.images[0]];
            //     attributes.canvas_image_id = result.images[0];
            //     canvasUploadUrl = uploadUrl;
            //     updatedAttrs['canvas-image'] = { type: 'images', id: attributes.canvas_image_id };
            // }
            let previewUrl = null;
            if (selectedFile && selectedLayer) {
                isUploadingNewRef.current = true;
                setOriginalImage(null)
                previewUrl = URL.createObjectURL(selectedFile);
                const response = await fetch(previewUrl);
                updatedAttrs.tempURL = previewUrl;

                const responseblob = await response.blob();
                const blob = new Blob([responseblob], { type: responseblob.type });
                blob.name = selectedFile.name;
                setTmpURL(previewUrl)
                const payload = await postMedium({
                    attrs: {
                        type: 'Image',
                        ownerType: "CelebrationPrintableLayer",
                        ownerId: destinationImageSrcExists.id,
                        filename: selectedFile.name,
                        previewFilename: ' ',
                    },
                });
                const { entities, result } = payload.data;
                const image = entities.images[result.images[0]];
                // Resolves rather than throwing, so a failed cover photo no longer
                // drops the whole save into the catch below and lose the other
                // attribute changes the user made on this screen.
                await uploadMedium({
                    url: image['upload-url'],
                    mediumId: image.id,
                    mediumType: 'Image',
                    file: selectedFile,
                });
                attributes.photo_id = result.images[0];
                updatedAttrs['photo'] = { type: 'images', id: attributes.photo_id };
                // Optimistically add the new image entity to the store with the preview URL
                // so RenderChild can display it immediately without waiting for revalidateCover
                update({
                    entityType: 'images',
                    id: attributes.photo_id,
                    attrs: { tmpURL: previewUrl },
                });
            }

            // if (canvasUploadUrl) {
            //     const previewUrl = URL.createObjectURL(blob);
            //     await uploadMediumFile(canvasUploadUrl, blob);
            // }

            await patch(
                `/v3/books/${firstBookId}/front_covers/${coverId}/front_cover_image_layers/${destinationImageSrcExists?.id}`,
                { data: { attributes } }
            );


            setIsDefaultStepSet(true);
            // revalidate();
            revalidateImage()
            revalidateCover();
            setRefreshInterval(3000);

            update({
                entityType: 'cover-layers',
                id: destinationImageSrcExists?.id,
                attrs: updatedAttrs,
            });
            setPhotoLayerOriginal({ ...destinationImageSrcExists.attributes, ...attributes, ...updatedAttrs })
            setPhotoLayer({ ...destinationImageSrcExists.attributes, ...attributes, ...updatedAttrs });
            isUploadingNewRef.current = false;
            setDestinationImageSrcExists({
                ...destinationImageSrcExists,
                attributes: { ...destinationImageSrcExists.attributes, ...attributes },
                relationships: {
                    ...destinationImageSrcExists.relationships,
                    ...(attributes.photo_id && {
                        photo: { data: { type: 'images', id: attributes.photo_id } }
                    }),
                },
                tmpURL: previewUrl,
            })

        } finally {
            isUploadingNewRef.current = false;
            // if (!(selectedFile && selectedLayer)) {

            setForceUploadMode(false);
            setSubmitting(false);
            setIsModalOpen(false);
            setSelectedLayer(null);
            setShowEditor(false);
            // }
        }
    };

    const onImageDeletion = async () => {
        const photoIdToDelete = destinationImageSrcExists?.relationships?.photo?.data?.id
        setSubmitting(true);
        await destroy(`/v2/media/${photoIdToDelete}`);

        await patch(`/v3/books/${firstBookId}/front_covers/${coverId}/front_cover_image_layers/${destinationImageSrcExists?.id}`,
            {
                data: {
                    attributes: { photo_id: null, canvas_image_id: null },
                },
            }
        )
        setSubmitting(false);
        receiveDeletion({ entityType: 'images', id: photoIdToDelete });

        update({
            entityType: 'cover-layers',
            id: destinationImageSrcExists?.id,
            attrs: {
                'canvas-image': {},
                'photo': {},
            },
        });
        setPhotoLayerOriginal({ ...photoLayer, 'canvas-image': {}, 'photo': {} })
        setPhotoLayer({
            ...photoLayer,
            'canvas-image': {},
            'photo': {},
        })

        setShowEditor(false);
        revalidateCover()
        setForceUploadMode(false);
    };

    useEffect(() => {
        if (photoLayer) {
            if (photoLayer?.width && photoLayer?.left !== null && photoLayer?.top !== null)
                setPhotoScalling({
                    x: photoLayer?.left / (photoLayer?.width || 1),
                    y: photoLayer?.top / (photoLayer?.height || 1),
                });
            setScale(photoLayer?.scale || 1);
        }
    }, [photoLayer]);

    function onImageReady() {
        setLoading(false);
    }

    return <>
        {(!tmpURL && (isSubmitting || uploadStatus === 'processing' ||
            uploadStatus === 'pending_upload' ||
            uploadStatus === 'uploaded')) ? (
            <LoadingBg
                sx={{
                    pointerEvents: 'none',
                    gridArea: '1 / 1 / 2 / 2',
                    height: `${props.height}px`,
                    borderRadius: '50px !important',
                    left: `${props.xpos}px`,
                    top: `${props.ypos}px`,
                    width: `${props.width}px`,
                    zIndex: 9,
                    position: 'absolute',
                }}
            >
                <RotatingText
                    className="loading"
                    fontSize="150px"
                    color="white"
                    textAlign="center"
                />
            </LoadingBg>
        ) : (
            <ImageEditingOptions
                showEditor={showEditor}
                destinationImageSrcExists={destinationImageSrcExists}
                bg="transparent"
                setShowEditor={setShowEditor}
                onImageDeletion={destinationImageSrcExists ? onImageDeletion : undefined}
                onEditingClick={() => {
                    const OriginalPhoto = (children || []).find(child => child?.attributes?.['component-value'] === "photo")
                    if (OriginalPhoto?.attributes) {
                        setPhotoLayerOriginal({
                            ...OriginalPhoto?.attributes, ...OriginalPhoto?.relationships
                        })
                        setPhotoLayer({ ...OriginalPhoto?.attributes, ...OriginalPhoto?.relationships })
                    }
                    setForceUploadMode(false);
                    setSelectedLayer(frameLayer);
                }}
                sx={{
                    marginTop: `-${props?.height / 2}px`,
                    boxShadow: 'unset'
                }}
                {...props}
            >

                <ImageLayer
                    destinationImageSrcExists={destinationImageSrcExists}
                    {...props}
                    children={children}

                    // children={(children || []).map(ch =>
                    //     ch.id === destinationImageSrcExists?.id ? destinationImageSrcExists : ch
                    // )}
                    photo={Object.keys(props?.['canvas-image']).length > 0 ? props?.['canvas-image'] : props?.['photo']}
                    zoom={zoom}
                    setSelectedLayer={setSelectedLayer}
                    selectedLayer={selectedLayer}
                    onImageReady={onImageReady}
                />
            </ImageEditingOptions >
        )}
        {(isModalOpen || isSubmitting) && <Modal
            title={(destinationImageSrcExists && !forceUploadMode) ? "Edit Image" : "Upload Image"}
            shadeProps={{ style: { zoom: `${zoom * 100}%` }, pt: [0, 4] }}
            close={{
                onClose: () => {
                    if (!isSubmitting) {
                        setIsModalOpen(false);
                        setSelectedLayer(null);
                        setForceUploadMode(false);
                    }
                }
            }}
            footerOffset={60}
        >
            <ImageUploader
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                src={uploaderSrc}
                frameSrc={src}
                width={props?.width}
                height={props?.height}
                photoScaling={photoScaling}
                scale={scale}
                onImageSelection={onImageSelection}
                setPhotoScalling={setPhotoScalling}
                onImageDeletion={destinationImageSrcExists ? onImageDeletion : undefined}
                setScale={setScale}
                isModalOpen={isModalOpen}
                isSubmitting={isSubmitting}
                setFile={setFile}
                bgColor={frontCoverEntity?.['background-colour']}
                setSubmitting={setSubmitting}
            />
        </Modal>
        }
    </>
}

function FrontRenderer({ id, firstBookId, selectedTemplate, setPhotoLayerOriginal, frontCoverId, zoom, setSelectedLayer, selectedLayer, values, frameLayer, setFrameLayer, photoLayer, setPhotoLayer, setShowHover, showHover, children, ...props }) {
    const { entity: frontTemplateLayer } = useEntity({ id, type: 'cover-layers' })

    useEffect(() => {
        if (frontTemplateLayer?.['component-variable'] === "frame")
            setFrameLayer(frontTemplateLayer)

    }, [frontTemplateLayer?.['component-variable'], frontTemplateLayer?.['component-value'], selectedTemplate])

    if (frontTemplateLayer?.['component-value'] === "hover" && !showHover)
        return null

    return frontTemplateLayer?.type === "Books::FrontCoverImageLayer" ? <Box id={frontTemplateLayer?.id}>
        <RenderImageLayer
            {...frontTemplateLayer}
            photo={Object.keys(frontTemplateLayer?.['canvas-image']).length > 0 ? frontTemplateLayer?.['canvas-image'] : frontTemplateLayer?.['photo']}
            zoom={zoom}
            setSelectedLayer={setSelectedLayer}
            selectedLayer={selectedLayer}
            firstBookId={firstBookId}
            coverId={frontCoverId}
            frameLayer={frameLayer}
            photoLayer={photoLayer}
            setPhotoLayer={setPhotoLayer}
            setPhotoLayerOriginal={setPhotoLayerOriginal}
            setShowHover={setShowHover}
            showHover={showHover}
            children={children}
            selectedTemplate={selectedTemplate}
            {...props}
        />
    </Box>
        : <Box id={frontTemplateLayer?.id}> <TextLayer
            {...frontTemplateLayer}
            zoom={zoom}
            xpos={frontTemplateLayer?.xpos}
            ypos={frontTemplateLayer?.ypos}
            fontAlignment={frontTemplateLayer?.['font-alignment']}
            fontFamily={frontTemplateLayer?.['font-name']}
            fontWeight={frontTemplateLayer?.['font-weight']}
            fontSize={frontTemplateLayer?.['font-size']}
            color={frontTemplateLayer?.['font-colour']}
            text={values[frontTemplateLayer?.['component-variable'].replace('_', '-')]}
        />
        </Box>
}

const RenderFrontCover = React.forwardRef(({ firstBookId, values, selectedTemplate, setIsDefaultStepSet, fronCoverLayers = [], photo }, ref) => {
    const reactpinchzoomRef = useRef()
    const [zoom, setZoom] = useState(10);
    const [frameLayer, setFrameLayer] = useState(null);
    const [photoLayer, setPhotoLayer] = useState(null);
    const [photoLayerOriginal, setPhotoLayerOriginal] = useState(null);

    const [photoSrc, setPhotoSrc] = useState(null);
    const [showHover, setShowHover] = useState(null);
    const [destinationImageSrcExists, setDestinationImageSrcExists] = useState(null);
    const [tmpURL, setTmpURL] = useState(null);

    const [forceUploadMode, setForceUploadMode] = useState(false);

    const { entity } = useEntity({ type: 'book', id: firstBookId })
    const { entity: frontCoverEntity } = useEntity({ id: entity?.['front-cover']?.id, type: 'front-covers' })
    const [selectedLayer, setSelectedLayer] = useState(null);

    useEffect(() => {
        if (reactpinchzoomRef.current) {
            const { zoomToElement } = reactpinchzoomRef.current;
            zoomToElement('templateContainer');
        }
    }, [reactpinchzoomRef?.current]);

    if (!frontCoverEntity?.width || !frontCoverEntity?.height)
        return null
    return <Flex flexDirection="column" justifyContent="center" alignItems="center" width="100%" height="100%">
        <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            ref={ref}
            sx={{
                zoom: `${zoom}% `,
                MozTransform: `scale(${zoom / 100})`,
                MozTransformOrigin: '10% 0',
                position: 'relative',
                boxShadow: '0px 7px 36px 5px rgba(0, 0, 0, 0.25)',
                width: `${frontCoverEntity?.width}px`,
                height: `${frontCoverEntity?.height}px`,
                left: 0,
                right: 0,
                m: 'auto',
                touchAction: 'pan-x pan-y pinch-zoom',
                bg: frontCoverEntity?.['background-colour']
            }}
            id="templateContainer"
        >
            {fronCoverLayers && nestImageLayers(fronCoverLayers).map(({ id, type, children }) => <Box key={id} id={id}>
                <FrontRenderer
                    setTmpURL={setTmpURL}
                    tmpURL={tmpURL}
                    setSelectedLayer={setSelectedLayer}
                    selectedLayer={selectedLayer}
                    firstBookId={firstBookId}
                    frontCoverId={frontCoverEntity?.id}
                    values={values}
                    frameLayer={frameLayer}
                    setFrameLayer={setFrameLayer}
                    photoLayer={photoLayer}
                    setPhotoLayer={setPhotoLayer}
                    photoLayerOriginal={photoLayerOriginal}
                    setPhotoLayerOriginal={setPhotoLayerOriginal}
                    setShowHover={setShowHover}
                    showHover={showHover}
                    selectedTemplate={selectedTemplate}
                    id={id}
                    type={type}
                    zoom={zoom}
                    children={children}
                    photoSrc={photoSrc}
                    setPhotoSrc={setPhotoSrc}
                    setIsDefaultStepSet={setIsDefaultStepSet}
                    forceUploadMode={forceUploadMode}
                    setForceUploadMode={setForceUploadMode}
                    destinationImageSrcExists={destinationImageSrcExists}
                    setDestinationImageSrcExists={setDestinationImageSrcExists}
                />
            </Box>
            )}
        </Flex>
        <Flex width="100%" mt={2} mb={1} alignItems="center" justifyContent="center">
            {destinationImageSrcExists && destinationImageSrcExists?.relationships?.['photo']?.data?.id && (
                <Flex
                    justifyContent="center"
                    alignItems="center"
                    sx={{ cursor: 'pointer' }}
                    mr={3}
                    onClick={() => {
                        setForceUploadMode(false);
                        // setPhotoLayer(photoLayerOriginal);
                        setPhotoLayer({ ...destinationImageSrcExists?.attributes, ...destinationImageSrcExists?.relationships })
                        setSelectedLayer(frameLayer);
                    }}
                >
                    <FiEdit color="#9B59B6" size={24} />
                    <Text ml="4px" color="#9B59B6" fontFamily="Roboto" fontSize="16px" fontWeight={[500, 400]} lineHeight="150%">Edit Photo</Text>
                </Flex>
            )}

            {frameLayer ? <Flex
                justifyContent="center"
                sx={{ cursor: 'pointer' }}
                alignItems="center"
                onClick={() => {
                    setForceUploadMode(true)
                    setSelectedLayer(frameLayer);
                }}
            >
                <FiImage color="#9B59B6" size={24} />
                <Text ml="4px" color="#9B59B6" fontFamily="Roboto" fontSize="16px" fontWeight={[500, 400]} lineHeight="150%">
                    {destinationImageSrcExists && destinationImageSrcExists?.relationships?.['photo']?.data?.id ? 'Update' : 'Add'} Photo
                </Text>
            </Flex> :
                <Text ml="4px" color="#9B59B6" fontFamily="Roboto" fontSize="16px" fontWeight={[500, 400]} lineHeight="150%">
                    Please select a template from the options below.
                </Text>
            }
        </Flex>
    </Flex>
})

export default RenderFrontCover;